import { AttioSort, AttioSortDirection, AttioValuationFilter, AttioValuationQueryBody } from 'dashboard409a/common/constants/analyst-dashboard';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import {
  AttioBulkNotesResponse,
  AttioValuationListQueryResponse,
  AttioValuationRecord,
  SimplifiedAttioValuation,
  SimplifiedAttioValuationResponse,
} from '../attio.types';
import { AttioConsultantService } from './AttioConsultantService';
import { AttioEngagementService } from './AttioEngagementService';
import { AttioNotesService } from './AttioNotesService';
import { BaseAttioService } from './BaseAttioService';
import { Note } from '../models.types';

// Service-specific types
export interface AttioValuationListParams {
  userRecordId: string;
  filters: AttioValuationFilter;
  limit?: number;
  offset?: number;
  searchParams: {searchParam: string, searchAll: boolean};
}

export interface AttioConsultantData {
  name: string;
  imageSrc: string;
}

export interface AttioEngagementManagerData {
  name: string;
  imageSrc: string;
}

// Type definitions for better type safety
type TableColumnId = 'company_name' | 'status' | 'valuation_date' | 'valuation_fee';
type AttioAttribute = 'identifier' | 'stage' | 'valuation_date' | 'valuation_fee';

// Column mapping configuration
const COLUMN_MAPPING: Record<TableColumnId, AttioAttribute> = {
  company_name: 'identifier',
  status: 'stage',
  valuation_date: 'valuation_date',
  valuation_fee: 'valuation_fee',
} as const;

export class AttioValuationService extends BaseAttioService {
  private readonly engagementService: AttioEngagementService;
  private readonly consultantService: AttioConsultantService;
  private readonly notesService: AttioNotesService;

  constructor() {
    super('AttioValuationService');
    this.engagementService = new AttioEngagementService();
    this.consultantService = new AttioConsultantService();
    this.notesService = new AttioNotesService();
  }

  /**
   * Map table column IDs to Attio attributes
   */
  static mapTableColumnToAttioAttribute(columnId: string): AttioAttribute | null {
    return COLUMN_MAPPING[columnId as TableColumnId] || null;
  }

  /**
   * Create Attio sort from table sorting
   */
  static createAttioSort(orderBy: string, order: AttioSortDirection): AttioSort | null {
    const attribute = AttioValuationService.mapTableColumnToAttioAttribute(orderBy);
    if (!attribute) {
      return null;
    }

    return {
      direction: order,
      attribute,
    };
  }

  /**
   * Create valuation query body
   */
  private static createValuationQueryBody(recordId: string, filters: AttioValuationFilter, limit: number, offset: number): AttioValuationQueryBody {
    // Extract sorts from filters to avoid duplication
    const { sorts, ...filterOnly } = filters;

    const body: AttioValuationQueryBody = {
      filter: {
        $or: [
          { valuation_lead_94: { target_record_id: recordId } },
          { other_consultant: { target_record_id: recordId } },
          { valuation_leads: { target_record_id: recordId } },
          { other_consultants: { target_record_id: recordId } },
          ...(filterOnly.$or || []),
        ],
        ...(filterOnly.$and && { $and: filterOnly.$and }),
      },
      limit,
      offset,
    };

    // Add sorting if provided
    if (sorts && sorts.length > 0) {
      body.sorts = sorts;
    }

    return body;
  }

  /**
   * Helper function to format currency values
   */
  private static formatCurrencyValue(currencyData: { currency_value?: number; currency_code?: string } | undefined): string | null {
    if (!currencyData || currencyData.currency_value === undefined || currencyData.currency_value === null) {
      return null;
    }
    return formatCurrency({ value: currencyData.currency_value, currency: currencyData.currency_code });
  }

  /**
   * Extract financial fields from a valuation record
   */
  private static extractFinancialFields(data: AttioValuationRecord['values']): Pick<
    SimplifiedAttioValuation,
    | 'net_value'
    | 'split'
    | 'payment_1'
    | 'pay_1_date'
    | 'payment_2'
    | 'pay_2_date'
    | 'payment_3'
    | 'pay_3_date'
    | 'refund'
    | 'refund_date'
    | 'due'
    | 'adjustment'
    | 'adjustment_date'
    | 'bad_debt'
    | 'bad_debt_date'
    | 'renewal_rate'
  > {
    return {
      net_value: this.formatCurrencyValue(data.net_value?.[0]),
      split: this.formatCurrencyValue(data.split?.[0]),
      payment_1: this.formatCurrencyValue(data.payment_1?.[0]),
      pay_1_date: data.pay_1_date?.[0]?.value || null,
      payment_2: this.formatCurrencyValue(data.payment_2?.[0]),
      pay_2_date: data.pay_2_date?.[0]?.value || null,
      payment_3: this.formatCurrencyValue(data.payment_3?.[0]),
      pay_3_date: data.pay_3_date?.[0]?.value || null,
      refund: this.formatCurrencyValue(data.refund?.[0]),
      refund_date: data.refund_date?.[0]?.value || null,
      due: this.formatCurrencyValue(data.due?.[0]),
      adjustment: this.formatCurrencyValue(data.adjustment?.[0]),
      adjustment_date: data.adjustment_date?.[0]?.value || null,
      bad_debt: this.formatCurrencyValue(data.bad_debt?.[0]),
      bad_debt_date: data.bad_debt_date?.[0]?.value || null,
      renewal_rate: this.formatCurrencyValue(data.renewal_rate?.[0]),
    };
  }

  /**
   * Get all valuations with bulk data (consultants, managers, deals) in one call
   */
  async getAllValuationsWithBulkData({
    userRecordId,
    filters,
    searchParams,
  }: Omit<AttioValuationListParams, 'limit' | 'offset'>): Promise<SimplifiedAttioValuationResponse> {
    try {
      const response = await this.post<SimplifiedAttioValuationResponse>(
        this.endpoints.retrieveValuationsWithBulkData,
        {
          user_record_id: userRecordId,
          filters,
          search_params: {search_param: searchParams.searchParam, search_all: searchParams.searchAll},
        }
      );
      return response;
    } catch {
      return {
        data: [],
        hasMore: false,
        totalCount: 0,
      };
    }
  }

  /**
   * Get all valuations for a user (for infinite scroll)
   */
  async getAllValuations({
    userRecordId,
    filters,
  }: Omit<AttioValuationListParams, 'limit' | 'offset'>): Promise<SimplifiedAttioValuationResponse> {
    try {
      const allValuations: SimplifiedAttioValuation[] = [];
      let offset = 0;
      const limit = 1000; // Attio's max limit
      let hasMore = true;

      while (hasMore) {
        const body = AttioValuationService.createValuationQueryBody(userRecordId, filters, limit, offset);
        // eslint-disable-next-line no-await-in-loop
        const rawData: AttioValuationListQueryResponse = await this.post(this.endpoints.retrieveValuationList, body);
        
        if (rawData.data.length === 0) {
          break;
        }

        // Transform basic data first (without additional data)
        const basicData = rawData.data.map(record => {
          const data = record.values;
          const feeData = data.valuation_fee?.[0];
          const { currency_value: value, currency_code: currency } = feeData || {};
          const valuationFee = value ? formatCurrency({ value, currency }) : null;
          
          return {
            company_name: data.identifier?.[0]?.value || null,
            status: data.stage?.[0]?.option?.title || null,
            notes: null, // Will be loaded later
            engagement_manager: null, // Will be loaded later
            engagement_manager_image_src: null, // Will be loaded later
            valuation_date: data.valuation_date?.[0]?.value || null,
            valuation_fee: valuationFee,
            record_id: record.id.record_id,
            created_at: record.created_at,
            deal_id: data.deal?.[0]?.target_record_id || null,
            other_consultant_id: data.other_consultants?.[0]?.target_record_id || data.other_consultant?.[0]?.target_record_id || null,
            engagement_manager_id: data.engagement_manager?.[0]?.target_record_id || null,
            other_consultant: null, // Will be loaded later
            other_consultant_image_src: null, // Will be loaded later
            deal: null, // Will be loaded later
            deal_due: null, // Will be loaded later
            deal_stage: null, // Will be loaded later
            // Financial fields
            ...AttioValuationService.extractFinancialFields(data),
          };
        });

        allValuations.push(...basicData);

        // If we got fewer records than the limit, we've reached the end
        if (rawData.data.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      return {
        data: allValuations,
        hasMore: false,
        totalCount: allValuations.length,
      };
    } catch {
      return {
        data: [],
        hasMore: false,
        totalCount: 0,
      };
    }
  }

  /**
   * Load notes in bulk for multiple valuations
   * Returns a Map with valuation IDs as keys and Note objects or null as values
   */
  async loadBulkNotes(attioValuationIds: string[]): Promise<Map<string, Note | null>> {
    try {
      const body = {
        attio_valuation_ids: attioValuationIds,
        reference_type: 'at',
      };
      
      // The endpoint now returns directly an object with valuation IDs as keys
      // Each value is either a Note object or null
      const response: AttioBulkNotesResponse = await this.post(this.endpoints.retrieveBulkNotes, body);

      const notesMap = new Map<string, Note | null>();
      
      if (response && typeof response === 'object') {
        Object.entries(response)
          .filter(([valuationId]) => valuationId)
          .forEach(([valuationId, note]) => {
            // The note object already has the correct structure (Note type)
            // or it's null if there's no note for this valuation
            notesMap.set(valuationId, note);
          });
      }

      return notesMap;
    } catch {
      return new Map();
    }
  }


  /**
   * Update a valuation record
   */
  async updateValuation(valuationId: string, valuation: SimplifiedAttioValuation): Promise<SimplifiedAttioValuation> {
    const response = await this.put<SimplifiedAttioValuation>(
      this.endpoints.retrieveValuationByRecordId(valuationId),
      valuation
    );
    return response;
  }

  /**
   * Search valuations
   */
  async searchValuationsByName(searchString: string): Promise<SimplifiedAttioValuation[]> {
    try {
      const response = await this.post<SimplifiedAttioValuationResponse>(
        this.endpoints.searchValuationsByName(),
        {
          query: searchString,
          objects: [
            'valuations'
          ],
          request_as: {
            type: 'workspace'
          },
          limit: 25
        }
      );
      return response.data;
    } catch {
      return [];
    }
  }
}